// ============================================================================
// Five steps to record a separation.
//
// ⛔ NOTHING IS WRITTEN AND NOBODY IS NOTIFIED UNTIL STEP 5. Every field below
// is held in local state until Start, because a half-filled separation that
// pinged the COO would be worse than no record at all.
//
// ⛔ PICK THE REASON, NOT THE FACT. Seven reasons, each of which drives
// something real — the checklist, access timing, severance availability and the
// rehire default. There is no "Retired" (TCS has no pension) and no plain
// "Separated": that is the name of the event, so it answers nothing.
//
// ⛔ THE SUPERVISOR IS ASKED FOR, NOT RESOLVED. There is no supervisor,
// manager or reports-to field on employees and positions holds zero rows, so
// the app proposes active employees sharing the leaver's work_state and a
// person picks. Whoever is picked gets the property and subscription groups and
// can hand any single item on from there.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, PageHeader, useToast } from '../../shared/components';
import { errorMessage } from '../../shared/errors';
import {
  fetchHoldings,
  fetchSupervisorCandidates,
  startSeparation,
  useEmployeePicker,
  type Holdings,
} from './hooks/use-separation-data';
import { SEPARATION_REASONS, linkedDay, linkedType, reasonSpec, type SeparationType } from './types';

const STEPS = [
  'Person & reason',
  'What they hold',
  'Pay & severance',
  'Documents',
  'Review & start',
];

function addDays(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function endOfMonth(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))
    .toISOString()
    .slice(0, 10);
}

export default function SeparationWizard() {
  const navigate = useNavigate();
  const toast = useToast();
  const { people } = useEmployeePicker();

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // Step 1. The person card sends the employee it was opened from as
  // ?employee=EID-NNNN (item 818); the picker still shows, so it can be changed.
  const [params] = useSearchParams();
  // Ask AI opens this form with what was said filled in (TCSD-0532): the kind
  // and the dates as ?type= / ?notice= / ?last_day=. Never the reason or
  // anything sensitive — those are typed here. Every field stays editable.
  const [employeeId, setEmployeeId] = useState(params.get('employee') ?? '');
  const [type, setType] = useState<SeparationType | ''>(linkedType(params.get('type')));
  const [noticeDate, setNoticeDate] = useState(linkedDay(params.get('notice')) || new Date().toISOString().slice(0, 10));
  const [lastDay, setLastDay] = useState(linkedDay(params.get('last_day')));
  const [supervisor, setSupervisor] = useState('');
  const [reason, setReason] = useState('');
  const [ceoAttested, setCeoAttested] = useState(false);
  const [ceoDate, setCeoDate] = useState('');
  const [ceoNote, setCeoNote] = useState('');
  const [rehire, setRehire] = useState('');
  const [trail, setTrail] = useState('');

  // Step 2
  const [holdings, setHoldings] = useState<Holdings | null>(null);
  const [candidates, setCandidates] = useState<
    Array<{ employee_id: string; display_name: string | null; employee_title: string | null; work_state: string | null }>
  >([]);

  // Step 3
  const [finalPayDate, setFinalPayDate] = useState('');
  const [sevOffered, setSevOffered] = useState(false);
  const [sevDays, setSevDays] = useState<number | ''>('');
  const [payThrough, setPayThrough] = useState('');
  const [benefitsThrough, setBenefitsThrough] = useState('');
  const [method, setMethod] = useState('');
  const [release, setRelease] = useState(false);
  const [nonSolicit, setNonSolicit] = useState(false);
  const [transition, setTransition] = useState(false);
  const [sevNote, setSevNote] = useState('');

  // Step 4
  const [folder, setFolder] = useState('');
  const [notes, setNotes] = useState('');

  const spec = reasonSpec(type || null);

  // Defaults follow the reason, because the reason is what decides them.
  useEffect(() => {
    if (!spec) return;
    setRehire(spec.rehireDefault === 'Not eligible' ? 'No' : spec.rehireDefault === 'n/a' ? '' : 'Yes');
    setSevOffered(spec.severance === 'expected');
    if (spec.immediate && !lastDay) setLastDay(new Date().toISOString().slice(0, 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (!sevOffered || !lastDay || !sevDays) return;
    const pay = addDays(lastDay, Number(sevDays));
    setPayThrough(pay);
    setBenefitsThrough(endOfMonth(pay));
  }, [sevOffered, lastDay, sevDays]);

  useEffect(() => {
    if (step !== 1 || !employeeId) return;
    void (async () => {
      try {
        const [h, c] = await Promise.all([
          fetchHoldings(employeeId),
          fetchSupervisorCandidates(employeeId),
        ]);
        setHoldings(h);
        setCandidates(c);
        if (!supervisor && c.length > 0) setSupervisor(c[0].employee_id);
      } catch {
        setHoldings(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, employeeId]);

  const step1Ready = useMemo(
    () =>
      !!employeeId &&
      !!type &&
      !!noticeDate &&
      !!lastDay &&
      !!reason.trim() &&
      (!spec?.ceoAttestationRequired || ceoAttested) &&
      (type !== 'Terminated_For_Cause' || !!trail),
    [employeeId, type, noticeDate, lastDay, reason, spec, ceoAttested, trail],
  );

  async function submit() {
    setBusy(true);
    try {
      const res = (await startSeparation({
        employee_id: employeeId,
        separation_type: type as string,
        notice_date: noticeDate,
        last_working_day: lastDay,
        supervisor_eid: supervisor || null,
        reason: reason.trim() || null,
        performance_trail_complete: trail || null,
        ceo_attested: ceoAttested,
        ceo_attested_on: ceoDate || null,
        ceo_attestation_note: ceoNote.trim() || null,
        eligible_for_rehire: rehire || null,
        final_pay_date: finalPayDate || null,
        severance_offered: sevOffered,
        severance_length_days: sevDays === '' ? null : Number(sevDays),
        severance_pay_through: sevOffered ? payThrough || null : null,
        severance_benefits_through: sevOffered ? benefitsThrough || null : null,
        severance_payment_method: sevOffered ? method || null : null,
        severance_release_required: sevOffered && release,
        severance_non_solicitation: sevOffered && nonSolicit,
        severance_transition: sevOffered && transition,
        severance_notes: sevNote.trim() || null,
        document_folder_url: folder.trim() || null,
        notes: notes.trim() || null,
      })) as { separation_id?: string; warning?: string | null };
      toast.success('Separation recorded', {
        description: res.warning ?? 'The COO has the approval; the CEO is watching.',
      });
      navigate(`/people/separations/${res.separation_id}`);
    } catch (err) {
      toast.error("Couldn't record it", { description: errorMessage(err, 'Try again.') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <PageHeader
        eyebrow="People › Separations"
        title="Record a separation"
        meta={`Step ${step + 1} of ${STEPS.length} · ${STEPS[step]}`}
      />

      <ol className="flex flex-wrap gap-2 text-xs">
        {STEPS.map((s, i) => (
          <li
            key={s}
            className={`rounded px-2 py-1 ${
              i === step
                ? 'bg-tcs-text text-tcs-surface'
                : i < step
                  ? 'bg-sentiment-positive-fill text-sentiment-positive-text'
                  : 'bg-tcs-bg-muted text-tcs-text-muted'
            }`}
          >
            {i + 1} · {s}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <Card className="space-y-4 p-5">
          <Field label="Who is leaving?" hint="Only current employees appear here. Anyone already separated is excluded.">
            <select
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              className={inputCls}
            >
              <option value="">Pick an employee…</option>
              {people.map((p) => (
                <option key={p.employee_id} value={p.employee_id}>
                  {p.display_name ?? p.employee_id}
                  {p.employee_title ? ` — ${p.employee_title}` : ''}
                </option>
              ))}
            </select>
          </Field>

          <div>
            <div className="text-sm font-medium text-tcs-text">Reason for separation</div>
            <p className="mt-0.5 text-xs text-tcs-text-secondary">
              This drives the checklist, the access timing, severance eligibility and
              rehire status. Pick the reason, not the fact.
            </p>
            <div className="mt-2 space-y-2">
              {SEPARATION_REASONS.map((r) => (
                <label
                  key={r.value}
                  className={`flex cursor-pointer items-start gap-2 rounded border p-3 ${
                    type === r.value ? 'border-tcs-text' : 'border-tcs-border'
                  }`}
                >
                  <input
                    type="radio"
                    name="separation-reason"
                    checked={type === r.value}
                    onChange={() => setType(r.value)}
                    className="mt-1"
                  />
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-tcs-text">{r.label}</span>
                      {r.immediate && <Tag tone="critical">Access revoked today</Tag>}
                      {r.rehireDefault !== 'n/a' && <Tag>{r.rehireDefault} for rehire</Tag>}
                      {r.severance === 'expected' && <Tag>Severance expected</Tag>}
                    </span>
                    <span className="mt-0.5 block text-xs text-tcs-text-secondary">
                      {r.blurb}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Notice given">
              <input type="date" value={noticeDate} onChange={(e) => setNoticeDate(e.target.value)} className={inputCls} />
            </Field>
            <Field
              label="Last working day"
              hint="Access stays on until this date. Two weeks' notice should not mean locked out today."
            >
              <input type="date" value={lastDay} onChange={(e) => setLastDay(e.target.value)} className={inputCls} />
            </Field>
          </div>

          <Field label="What happened" hint="Plain language. Read by the approver and by whoever screens them for rehire later.">
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className={inputCls} />
          </Field>

          {type === 'Terminated_For_Cause' && (
            <Field
              label="Is the performance trail complete?"
              hint="A missing trail is RECORDED, not blocking. Blocking a real termination on paperwork guarantees the form gets bypassed and the event never gets recorded at all."
            >
              <select value={trail} onChange={(e) => setTrail(e.target.value)} className={inputCls}>
                <option value="">Answer this…</option>
                <option value="Yes">Yes — counselling and warnings are documented</option>
                <option value="No">No — the documentation is thin or missing</option>
              </select>
            </Field>
          )}

          <div className={`rounded border p-3 ${spec?.ceoAttestationRequired ? 'border-tcs-text' : 'border-tcs-border'}`}>
            <label className="flex items-start gap-2 text-sm text-tcs-text">
              <input type="checkbox" checked={ceoAttested} onChange={(e) => setCeoAttested(e.target.checked)} className="mt-1" />
              <span>
                Discussed with the CEO
                {spec?.ceoAttestationRequired && (
                  <span className="ml-1 text-sentiment-critical-text">— required for this reason</span>
                )}
                <span className="mt-0.5 block text-xs text-tcs-text-secondary">
                  The CEO is involved in every termination but does not use the app. This
                  attestation, recorded by the COO, is the governance record. There is no
                  blocking in-app CEO approval.
                </span>
              </span>
            </label>
            {ceoAttested && (
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <input type="date" value={ceoDate} onChange={(e) => setCeoDate(e.target.value)} className={inputCls} aria-label="Date discussed with the CEO" />
                <input value={ceoNote} onChange={(e) => setCeoNote(e.target.value)} placeholder="Note — optional" className={inputCls} aria-label="CEO attestation note" />
              </div>
            )}
          </div>

          <Field label="Eligible for rehire">
            <select value={rehire} onChange={(e) => setRehire(e.target.value)} className={inputCls}>
              <option value="">Not recorded</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </Field>
          <p className="text-xs text-tcs-text-muted">
            Nothing is written and nobody is notified until step 5.
          </p>
        </Card>
      )}

      {step === 1 && (
        <Card className="space-y-4 p-5">
          <h2 className="text-sm font-semibold text-tcs-text">What they hold</h2>
          <p className="text-xs text-tcs-text-secondary">
            Read live, right now. This is what the checklist will be built from.
          </p>
          {holdings ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat n={holdings.facilities} label="Facilities managed" />
              <Stat n={holdings.route_stops} label="Active route stops" />
              <Stat n={holdings.pipeline_cards} label="Pipeline cards" />
              <Stat n={holdings.vehicles} label="Vehicles" />
              <Stat n={holdings.cards} label={holdings.card_last4 ? `Card ·${holdings.card_last4}` : 'Company cards'} />
              <Stat n={holdings.open_tasks} label="Open tasks" />
              <Stat n={holdings.routings} label="Records routed to them" />
              <Stat n={holdings.notifications} label="Active notifications" />
              <Stat n={holdings.follows} label="Records followed" />
              <Stat n={holdings.partner_onboardings} label="Open partner onboardings" />
              <Stat n={holdings.app_profile} label="TCS app login" />
            </div>
          ) : (
            <p className="text-sm text-tcs-text-secondary">Reading their holdings…</p>
          )}

          {holdings && holdings.cards === 0 && (
            <p className="rounded bg-sentiment-warning-fill p-2 text-xs text-tcs-text-secondary">
              No company card row on file — and that is <strong>not</strong> evidence there
              is no card. Every US-based company-domain employee carries one except HR and
              Finance, and card coverage in the app is incomplete. The checklist will ask
              the COO to verify against the card program directly.
            </p>
          )}

          <Field
            label="Supervisor for the handover"
            hint="Who is physically closest and can take the property and subscription items? Proposed from active employees sharing their work state — change it to anyone active."
          >
            <select value={supervisor} onChange={(e) => setSupervisor(e.target.value)} className={inputCls}>
              <option value="">Pick a supervisor…</option>
              {candidates.map((c) => (
                <option key={c.employee_id} value={c.employee_id}>
                  {c.display_name ?? c.employee_id}
                  {c.employee_title ? ` — ${c.employee_title}` : ''}
                  {c.work_state ? ` · ${c.work_state}` : ''}
                </option>
              ))}
            </select>
          </Field>
        </Card>
      )}

      {step === 2 && (
        <Card className="space-y-4 p-5">
          <h2 className="text-sm font-semibold text-tcs-text">Final pay, and severance if we are offering it</h2>
          <Field label="Final paycheck date">
            <input type="date" value={finalPayDate} onChange={(e) => setFinalPayDate(e.target.value)} className={inputCls} />
          </Field>

          <label className="flex items-center gap-2 text-sm text-tcs-text">
            <input type="checkbox" checked={sevOffered} onChange={(e) => setSevOffered(e.target.checked)} />
            Offering severance
          </label>
          {spec?.severance === 'discouraged' && sevOffered && (
            <p className="rounded bg-sentiment-warning-fill p-2 text-xs text-tcs-text-secondary">
              Severance on a {spec.label.toLowerCase()} needs a note explaining why. It will
              be refused without one.
            </p>
          )}

          {sevOffered && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                {[30, 60, 90].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setSevDays(d)}
                    className={`rounded border p-2 text-left text-sm ${sevDays === d ? 'border-tcs-text' : 'border-tcs-border'}`}
                  >
                    <div className="font-medium text-tcs-text">{d} days</div>
                    {lastDay && (
                      <div className="text-xs text-tcs-text-muted">
                        Through {addDays(lastDay, d)}
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Pay continues through" hint="Compensation ends on THIS date, not the last working day.">
                  <input type="date" value={payThrough} onChange={(e) => setPayThrough(e.target.value)} className={inputCls} />
                </Field>
                <Field label="Benefits continue through" hint="Often month end, not the pay date. This is the field that gets it wrong — COBRA keys off it.">
                  <input type="date" value={benefitsThrough} onChange={(e) => setBenefitsThrough(e.target.value)} className={inputCls} />
                </Field>
              </div>
              <Field label="How it is paid">
                <select value={method} onChange={(e) => setMethod(e.target.value)} className={inputCls}>
                  <option value="">Pick one…</option>
                  <option value="Continued_Payroll">Continued payroll — simpler for benefits continuity</option>
                  <option value="Lump_Sum">Lump sum — cleaner break; benefits need separate handling</option>
                </select>
              </Field>
              <div className="space-y-2">
                <Check label="Requires a signed separation agreement and release" hint="Generates a checklist item. No severance pays until it is signed." checked={release} onChange={setRelease} />
                <Check label="Non-solicitation of TCS accounts and subcontractors" checked={nonSolicit} onChange={setNonSolicit} />
                <Check label="Transition assistance expected" checked={transition} onChange={setTransition} />
              </div>
              <Field label="Note">
                <textarea value={sevNote} onChange={(e) => setSevNote(e.target.value)} rows={2} className={inputCls} />
              </Field>
              {lastDay && payThrough && benefitsThrough && (
                <p className="rounded bg-tcs-bg-muted p-2 text-xs text-tcs-text-secondary">
                  Three dates, three effects. Access and reassignment end{' '}
                  <strong>{lastDay}</strong>; compensation ends <strong>{payThrough}</strong>;
                  benefits terminate and COBRA keys off <strong>{benefitsThrough}</strong>.
                </p>
              )}
            </>
          )}
        </Card>
      )}

      {step === 3 && (
        <Card className="space-y-4 p-5">
          <h2 className="text-sm font-semibold text-tcs-text">Documents</h2>
          <Field
            label="Separation document folder"
            hint="Counselling records, warning letters, the resignation email, cause documentation, signed acknowledgements. Documents stay in Drive — the app stores the pointer."
          >
            <input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="https://drive.google.com/…" className={inputCls} />
          </Field>
          <Field label="Anything else worth recording">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={inputCls} />
          </Field>
        </Card>
      )}

      {step === 4 && (
        <Card className="space-y-3 p-5 text-sm">
          <h2 className="text-sm font-semibold text-tcs-text">Review</h2>
          <Row k="Who" v={people.find((p) => p.employee_id === employeeId)?.display_name ?? employeeId} />
          <Row k="Reason" v={spec?.label ?? '—'} />
          <Row k="Notice / last working day" v={`${noticeDate} → ${lastDay}`} />
          <Row k="Access ends" v={spec?.immediate ? 'On submit — today' : lastDay} />
          <Row k="Supervisor" v={candidates.find((c) => c.employee_id === supervisor)?.display_name ?? supervisor ?? '—'} />
          <Row k="Discussed with the CEO" v={ceoAttested ? `Yes${ceoDate ? ` — ${ceoDate}` : ''}` : 'No'} />
          <Row k="Eligible for rehire" v={rehire || 'Not recorded'} />
          {type === 'Terminated_For_Cause' && <Row k="Performance trail complete" v={trail || 'Unanswered'} />}
          {sevOffered && (
            <>
              <Row k="Severance" v={`${sevDays || '—'} days · ${method || 'method not set'}`} />
              <Row k="Pay through / benefits through" v={`${payThrough || '—'} / ${benefitsThrough || '—'}`} />
            </>
          )}
          <p className="pt-2 text-xs text-tcs-text-secondary">
            On Start: the checklist is built from what they hold, the approval goes to the
            COO, and the CEO is subscribed as a watcher. Nothing is ever sent to the
            departing employee.
          </p>
        </Card>
      )}

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="secondary" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
          Back
        </Button>
        {step < 4 ? (
          <Button disabled={step === 0 && !step1Ready} onClick={() => setStep((s) => s + 1)}>
            Next: {STEPS[step + 1]}
          </Button>
        ) : (
          <Button loading={busy} disabled={!step1Ready} onClick={() => void submit()}>
            Start the separation
          </Button>
        )}
      </div>
    </div>
  );
}

const inputCls =
  'mt-1 w-full rounded border border-tcs-border bg-tcs-surface px-2 py-1.5 text-sm text-tcs-text';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-tcs-text">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-tcs-text-secondary">{hint}</span>}
    </label>
  );
}

function Check({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2 text-sm text-tcs-text">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1" />
      <span>
        {label}
        {hint && <span className="mt-0.5 block text-xs text-tcs-text-secondary">{hint}</span>}
      </span>
    </label>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div className="rounded border border-tcs-border p-2">
      <div className="text-lg font-semibold text-tcs-text">{n}</div>
      <div className="text-xs text-tcs-text-secondary">{label}</div>
    </div>
  );
}

function Tag({ children, tone }: { children: React.ReactNode; tone?: 'critical' }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-xs ${
        tone === 'critical'
          ? 'bg-sentiment-critical-fill text-sentiment-critical-text'
          : 'bg-tcs-bg-muted text-tcs-text-secondary'
      }`}
    >
      {children}
    </span>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-tcs-border py-1">
      <span className="text-tcs-text-secondary">{k}</span>
      <span className="text-right text-tcs-text">{v}</span>
    </div>
  );
}
